import Cookies from 'js-cookie';
import { Style } from '../component/elements/StyleJigsaw';
import { translations } from './translations';
import { VoiceDemoDataType } from './types';

export const getRandomText = (
  languageCode: keyof typeof translations,
  idx?: number,
): string => {
  const sampleParagraphs = translations[languageCode] ?? translations.en;
  return sampleParagraphs[
    // Sonar Ignore: Using Math.random() here is safe because cryptographic security is not required.
    Math.floor(idx ?? Math.random() * sampleParagraphs.length)
  ];
};

interface SSMLOptions {
  emotion?: string;
  cadence?: number;
  pitch?: number;
  speed?: number;
}

const escapeSSMLChars = (text: string) =>
  text.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');

export const textToSSML = (text: string, options: SSMLOptions = {}) => {
  const { emotion, cadence, pitch, speed } = options;

  let ssml = escapeSSMLChars(text);

  const emotionAttributes = [
    emotion ? `emotion="${emotion}"` : '',
    cadence ? `cadence="${cadence.toFixed(0)}%"` : '',
  ].filter(Boolean);
  if (emotionAttributes.length) {
    ssml = `<speechify:emotion ${emotionAttributes.join(
      ' ',
    )}>${ssml}</speechify:emotion>`;
  }

  const prosodyAttributes = [
    pitch ? `pitch="${pitch.toFixed(0)}%"` : '',
    speed ? `rate="${speed.toFixed(0)}%"` : '',
  ].filter(Boolean);
  if (prosodyAttributes.length) {
    ssml = `<prosody ${prosodyAttributes.join(' ')}>${ssml}</prosody>`;
  }

  return `<speak>${ssml}</speak>`;
};

async function generateSHA256(input: string) {
  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

export async function getStaticAudioFileName(
  voiceId: string,
  locale: string,
  style: string,
  text: string,
): Promise<string> {
  const textHash = await generateSHA256(text);
  return `${voiceId}_${locale}_${style}_${textHash}.mp3`;
}

export const getEmotionFromStyle = (style?: Style | null) => {
  if (!style) return 'bright';
  if (style === 'Podcast') return 'warm';
  else if (style === 'Training') return 'cheerful';
  else return 'assertive';
};

export const isAcceptedVoiceStylePair = (voiceName: string, style: string) => {
  if (style === 'Youtube' && voiceName === 'Kristy') {
    return true;
  }
  if (style === 'Podcast' && voiceName === 'Carly') {
    return true;
  }
  if (style === 'Training' && voiceName === 'Oliver') {
    return true;
  }
  return false;
};

export const getVoicePreferenceFromStyle = (
  voices: VoiceDemoDataType['voices'],
  style: string,
) => {
  const selectedVoice = voices.find((voice) => {
    if (isAcceptedVoiceStylePair(voice.displayName, style)) {
      return true;
    }
  });
  return selectedVoice;
};

export const getDefaultVoice = (voices: VoiceDemoDataType['voices']) => {
  return voices.find((voice) => {
    if (voice.displayName === 'Oliver') return true;
  });
};

export const getCountryNameFromLanguageCode = (languageCode: string) => {
  try {
    const regionCode = languageCode.split('-')[1];
    if (!regionCode) return null;

    const regionName = new Intl.DisplayNames(['en'], { type: 'region' });
    return regionName.of(regionCode);
  } catch (error) {
    console.error('Error getting country name:', error);
    return null;
  }
};

export const countLanguages = (languageCodes: string[]) => {
  const languageCount: Record<string, number> = {};

  languageCodes.forEach((code) => {
    const languagePrefix = code.split('-')[0];
    if (languageCount[languagePrefix]) {
      languageCount[languagePrefix]++;
    } else {
      languageCount[languagePrefix] = 1;
    }
  });

  return languageCount;
};

export const getRandomNumberInRange = (min: number, max: number) => {
  if (min > max) {
    throw new Error('Min value cannot be greater than max value');
  }
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

export const decodeJWT = (token: string) => {
  const payloadBase64 = token.split('.')[1];
  const payload = JSON.parse(atob(payloadBase64));
  return payload;
};

export const isTokenExpired = (token: string) => {
  const payload = decodeJWT(token);
  const currentTime = Math.floor(Date.now() / 1000);
  return payload.exp < currentTime;
};

export const getUserIdOrAnonymousId = async (): Promise<string> => {
  const userId = Cookies.get('ajs_user_id');
  if (userId && userId.trim() !== 'null') {
    return userId;
  }
  const anonymousId = Cookies.get('ajs_anonymous_id');
  if (anonymousId && anonymousId.trim() !== 'null') {
    return anonymousId;
  } else if (window.analytics.instance) {
    return window.analytics.instance.user().anonymousId();
  } else {
    console.warn('analytics not found');
    throw new Error('analytics not found');
  }
};

export function carryParams(
  baseUrl: string,
  extra?: Record<string, string | number>,
): string {
  if (typeof window === 'undefined') return baseUrl;

  const url = new URL(baseUrl, window.location.origin);

  const existingParams = new URLSearchParams(window.location.search);
  existingParams.forEach((value, key) => url.searchParams.set(key, value));

  if (extra) {
    Object.entries(extra).forEach(([key, val]) =>
      url.searchParams.set(key, val.toString()),
    );
  }

  return url.toString();
}

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
export const retryWithDelay = async <T>(
  fn: () => Promise<T>,
  numRetries: number,
  delayTime: number,
  initialDelay: number,
): Promise<T> => {
  for (let retriesLeft = numRetries; retriesLeft > 0; retriesLeft--) {
    try {
      await delay(retriesLeft === numRetries ? initialDelay : delayTime);
      return await fn();
    } catch (e) {
      if (retriesLeft === 0) throw new Error('Failed to fetch details');
    }
  }
  throw new Error('Failed to fetch details');
};
